import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AsyncSelect from 'react-select/async';
import useAdminConfig from '../hooks/useAdminConfig';
import { adminService } from '../services/admin';
import { userActions } from '../store/actions/user.actions';

const AdminTabs = ({ tabs, activeTab, onTabChange }) => (
  <div className='border-b border-gray-200 mb-6'>
    <nav className='flex space-x-8' aria-label='Tabs'>
      {tabs.map((tab, index) => (
        <button
          key={tab.id}
          type='button'
          onClick={() => onTabChange(index)}
          className={`
            py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200
            ${
              activeTab === index
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }
          `}
        >
          <div className='flex items-center gap-2'>
            {tab.icon && (
              <FontAwesomeIcon
                icon={tab.icon}
                className={activeTab === index ? 'text-blue-600' : 'text-gray-400'}
              />
            )}
            <span>{tab.label}</span>
          </div>
        </button>
      ))}
    </nav>
  </div>
);

const UserManagementTab = ({
  loadUserOptions,
  selectedUser,
  handleUserChange,
  handleLoginAsUser,
  isLoginAsUserLoading,
}) => (
  <div className='space-y-6'>
    <div className='bg-white rounded-lg border border-gray-200 p-6'>
      <h2 className='text-lg font-semibold text-gray-900 mb-4'>
        User Impersonation
      </h2>

      <div className='bg-amber-50 rounded-md border border-amber-200 p-4 mb-5'>
        <div className='flex gap-3'>
          <div className='flex-shrink-0'>
            <div className='w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center'>
              <FontAwesomeIcon
                icon='exclamation-circle'
                className='text-amber-600'
                size='sm'
              />
            </div>
          </div>
          <div>
            <h3 className='text-sm font-semibold text-amber-900 mb-1'>
              Production Environment Access
            </h3>
            <p className='text-sm text-amber-800'>
              You will log in as the real user. All actions are permanent and
              attributed to them.
            </p>
          </div>
        </div>
      </div>

      <div className='space-y-4'>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            Search User
          </label>
          <AsyncSelect
            cacheOptions
            defaultOptions={false}
            loadOptions={loadUserOptions}
            onChange={handleUserChange}
            value={selectedUser}
            placeholder='Type to search by email or username...'
            isClearable
            className='select-filter'
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              control: (base) => ({
                ...base,
                minHeight: '42px',
              }),
            }}
            menuPortalTarget={document.body}
            noOptionsMessage={({ inputValue }) =>
              inputValue.length < 2
                ? 'Type at least 2 characters to search'
                : 'No users found'
            }
          />
        </div>

        {selectedUser && (
          <div className='bg-gray-50 rounded-md p-3 border border-gray-200'>
            <p className='text-sm text-gray-700 mb-3'>
              <span className='font-medium'>Selected User:</span>{' '}
              {selectedUser.user.email || selectedUser.user.username}
              {selectedUser.user.name && ` (${selectedUser.user.name})`}
            </p>
            {selectedUser.therapist ? (
              <div className='space-y-2 text-xs'>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <span className='text-gray-500'>Therapist ID:</span>{' '}
                    <span className='font-mono text-gray-700'>
                      {selectedUser.therapist.id}
                    </span>
                  </div>
                  <div>
                    <span className='text-gray-500'>Therapist Name:</span>{' '}
                    <span className='text-gray-700'>
                      {selectedUser.therapist.firstname || ''}{' '}
                      {selectedUser.therapist.lastname || ''}
                    </span>
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <span className='text-gray-500'>User ID:</span>{' '}
                    <span className='font-mono text-gray-700'>
                      {selectedUser.value}
                    </span>
                  </div>
                  <div>
                    <span className='text-gray-500'>Organization ID:</span>{' '}
                    <span className='font-mono text-gray-700'>
                      {selectedUser.therapist.organizationId}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className='text-xs'>
                <div className='mb-2'>
                  <span className='text-gray-500'>User ID:</span>{' '}
                  <span className='font-mono text-gray-700'>
                    {selectedUser.value}
                  </span>
                </div>
                <div className='text-gray-500 italic'>No therapist associated</div>
              </div>
            )}
          </div>
        )}

        <button
          type='button'
          className='w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2.5 px-4 rounded-md transition-colors flex items-center justify-center gap-2'
          onClick={handleLoginAsUser}
          disabled={!selectedUser || isLoginAsUserLoading}
        >
          {isLoginAsUserLoading ? (
            <>
              <FontAwesomeIcon icon='spinner' spin />
              Logging in...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon='sign-out-alt' />
              Login as Selected User
            </>
          )}
        </button>
      </div>
    </div>

    {/* Future: Add more user management features here */}
  </div>
);

const BillingConfigTab = ({
  isLoading,
  adminConfig,
  updateEnableBilling,
  updateTakeBillingIntoAccount,
}) => {
  if (isLoading) {
    return (
      <div className='bg-white rounded-lg border border-gray-200 p-6'>
        <div className='flex items-center justify-center h-40'>
          <div className='text-lg text-gray-600'>Loading...</div>
        </div>
      </div>
    );
  }

  if (!adminConfig) {
    return (
      <div className='bg-white rounded-lg border border-gray-200 p-6'>
        <div className='flex items-center justify-center h-40'>
          <div className='text-lg text-gray-600'>
            No admin configuration available...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-white rounded-lg border-2 border-red-300 p-6'>
      <div className='flex items-start gap-3 mb-4'>
        <FontAwesomeIcon
          icon='exclamation-triangle'
          className='text-red-500 mt-0.5'
          size='lg'
        />
        <div>
          <div className='flex items-center gap-2 mb-1'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Billing Configuration
            </h2>
            <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700 border border-red-300'>
              Danger Zone
            </span>
          </div>
          <p className='text-sm text-gray-600 mb-1'>
            Control payment processing and billing enforcement across the
            platform.
          </p>
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-6'>
        <div>
          <label className='block text-sm font-medium text-gray-900 mb-1'>
            Payment Processing
          </label>
          <p className='text-xs text-gray-600 mb-3'>
            {adminConfig?.enableBilling
              ? 'Payments are currently enabled'
              : 'Payments are currently disabled'}
          </p>
          <button
            type='button'
            className={`w-full ${
              adminConfig?.enableBilling
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-green-600 hover:bg-green-700'
            } text-white font-medium py-2.5 px-4 rounded-md transition-colors`}
            onClick={updateEnableBilling}
            disabled={isLoading}
          >
            {adminConfig?.enableBilling
              ? 'Disable Payments'
              : 'Enable Payments'}
          </button>
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-900 mb-1'>
            Billing Enforcement
          </label>
          <p className='text-xs text-gray-600 mb-3'>
            {adminConfig?.takeBillingIntoAccount
              ? 'Currently taking billing into account'
              : 'Currently not taking billing into account'}
          </p>
          <button
            type='button'
            className={`w-full ${
              adminConfig?.takeBillingIntoAccount
                ? 'bg-orange-600 hover:bg-orange-700'
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white font-medium py-2.5 px-4 rounded-md transition-colors`}
            onClick={updateTakeBillingIntoAccount}
            disabled={isLoading}
          >
            {adminConfig?.takeBillingIntoAccount
              ? 'Stop Taking Billing Into Account'
              : 'Start Taking Billing Into Account'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminPage = ({ loginAsUser, authentication }) => {
  const {
    isLoading,
    adminConfig,
    updateTakeBillingIntoAccount,
    updateEnableBilling,
    getAdminConfig,
  } = useAdminConfig();

  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoginAsUserLoading, setIsLoginAsUserLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const { foundUser } = authentication || {};
  const roles = useMemo(
    () => foundUser?.['role-assignment'] || [],
    [foundUser]
  );

  useEffect(() => {
    getAdminConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserOptions = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      return [];
    }

    const users = await adminService.searchUsers(inputValue);
    return users.map((user) => {
      let therapistName = 'No therapist';
      if (user.therapist) {
        const firstName = user.therapist.firstname || '';
        const lastName = user.therapist.lastname || '';
        therapistName = `${firstName} ${lastName}`.trim() || 'Unnamed therapist';
      }

      const userIdentifier = user.email || user.username;
      const userName = user.name ? ` (${user.name})` : '';

      return {
        value: user.id,
        label: `${userIdentifier}${userName} - ${therapistName}`,
        user,
        therapist: user.therapist,
      };
    });
  }, []);

  const handleUserChange = (option) => {
    setSelectedUser(option);
  };

  const handleLoginAsUser = async () => {
    if (!selectedUser) {
      // eslint-disable-next-line no-alert
      alert('Please select a user first');
      return;
    }

    setIsLoginAsUserLoading(true);
    try {
      await loginAsUser(selectedUser.value);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error logging in as user:', error);
    } finally {
      setIsLoginAsUserLoading(false);
    }
  };

  const tabs = useMemo(() => {
    const allTabs = [
      {
        id: 'user-management',
        label: 'User Management',
        icon: 'users-cog',
        allowedRoles: ['super-admin'],
      },
      {
        id: 'billing-config',
        label: 'Billing & Payments',
        icon: 'file-invoice-dollar',
        allowedRoles: ['super-admin'],
      },
      {
        id: 'system-config',
        label: 'System Settings',
        icon: 'cog',
        allowedRoles: ['super-admin'],
      },
    ];

    return allTabs.filter((tab) => {
      if (!tab.allowedRoles) return true;
      return roles.some((role) => tab.allowedRoles.includes(role.roleName));
    });
  }, [roles]);

  const renderTabContent = (tabId) => {
    switch (tabId) {
      case 'user-management':
        return (
          <UserManagementTab
            loadUserOptions={loadUserOptions}
            selectedUser={selectedUser}
            handleUserChange={handleUserChange}
            handleLoginAsUser={handleLoginAsUser}
            isLoginAsUserLoading={isLoginAsUserLoading}
          />
        );
      case 'billing-config':
        return (
          <BillingConfigTab
            isLoading={isLoading}
            adminConfig={adminConfig}
            updateEnableBilling={updateEnableBilling}
            updateTakeBillingIntoAccount={updateTakeBillingIntoAccount}
          />
        );
      case 'system-config':
        return (
          <div className='bg-white rounded-lg border border-gray-200 p-6'>
            <h2 className='text-lg font-semibold text-gray-900 mb-4'>
              System Settings
            </h2>
            <p className='text-sm text-gray-600'>
              System configuration options will be added here.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  if (tabs.length === 0) {
    return (
      <div className='organizations-wrapper'>
        <div className='flex items-center justify-center h-64'>
          <div className='text-lg text-gray-600'>
            You do not have permission to access this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='organizations-wrapper'>
      <div className='mb-6'>
        <div className='flex items-center gap-3 mb-3'>
          <h1 className='text-3xl font-bold text-gray-900'>
            Admin Configuration
          </h1>
          <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300'>
            <FontAwesomeIcon icon='shield-alt' className='mr-1.5' size='sm' />
            Admin Only
          </span>
        </div>
        <p className='text-sm text-gray-600'>
          System-wide administrative settings. Please proceed with caution.
        </p>
      </div>

      <AdminTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      <div className='mt-6'>{renderTabContent(tabs[activeTab]?.id)}</div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    authentication: state.authentication,
  };
}

const mapDispatchToProps = {
  loginAsUser: userActions.loginAsUser,
};

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(AdminPage),
);
